/**
 * Window wording for leave-health patterns.
 *
 * The detector clamps every rolling window at 1 January (calendar-year
 * grounding, `_in_window` in `services/api/src/domain/leave_pattern_preview.py`),
 * so a count never reaches into last year. The copy must say what was counted:
 *  - 365-day windows always reach 1 Jan → "this year".
 *  - 90-day windows reach 1 Jan during Q1 → "this year"; from April they are a
 *    true trailing window → "in the last 90 days".
 * Mirrors `window_period` on the api so every surface reads the same.
 */

/** Window length (days) behind each pattern's copy, per the pattern catalog. */
export const PATTERN_WINDOW_DAYS: Readonly<Record<string, number>> = {
  frequent_sick_leave: 90,
  long_weekend_pattern: 90,
  poor_planning_emergency: 365,
  unpaid_leave_taken: 365,
};

/** Whether a `windowDays` look-back from `today` is cut short at 1 January. */
export function windowClampedToYearStart(
  windowDays: number,
  today: Date = new Date(),
): boolean {
  const lowerEdge = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - windowDays,
  );
  return lowerEdge < new Date(today.getFullYear(), 0, 1);
}

/** "this year" when the window is clamped at 1 Jan, else "in the last N days". */
export function windowPeriod(
  windowDays: number,
  today: Date = new Date(),
): string {
  return windowClampedToYearStart(windowDays, today)
    ? "this year"
    : `in the last ${windowDays} days`;
}
